import { Component, ComponentFactoryResolver, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ChartComponent } from 'ng-apexcharts';
import { Subscription } from 'rxjs';
import { DictionaryService } from 'src/app/dictionary';
import { ChartOptions } from 'src/app/_helpers/chartOptions';
import { DateStiPipe } from 'src/app/_pipes/date-sti.pipe';
import { UserService } from 'src/app/_services';
import { ThisPlatform } from 'src/app/_services/platform.service';
import { RowsLoaderService } from 'src/app/_services/rows-loader.service';
import { ReportStatus } from 'src/app/_types/report-status.enum';
import { BuildingService } from '../../building/building.service';
import { ReportFilterModalComponent } from '../../report/report-filter-modal/report-filter-modal.component';
import { ReportViewComponent } from '../../report/report-view/report-view.component';
import { Report } from '../../report/report.model';
import { ReportService } from '../../report/report.service';
import { PaginationClientSideOptions } from '../../_component/pagination-client-side/pagination-client-side-options';
import { PaginationColumn } from '../../_component/pagination-client-side/pagination-column.class';
import { preventSwipeToClose } from 'src/app/_helpers/preventSwipeToClose';

@Component({
  selector: 'app-submissions-report',
  templateUrl: './submissions-report.component.html',
  styleUrls: ['./submissions-report.component.scss'],
})
export class SubmissionsReportComponent implements OnInit {

  rowsLoader: RowsLoaderService;
  showLoader: boolean = true;
  reportsFilter: Report[];

  @ViewChild("chart") chart: ChartComponent;

  componentRef: any = ReportViewComponent;
  
  columnsDefinition: PaginationColumn[] = [
    {
      name: "number",
      displayName: "NUMBER",
      size: 4,
      sortable: true,
    },
    {
      name: "createdBy",
      displayName: "REPORT.USER",
      size: 4,
      sortable: true,
      colValue: (row) => {
        return row.createdBy.firstName + row.createdBy.lastName;
      },
    },
    {
      name: "createdAt",
      displayName: "REPORT.DATE",
      size: 4,
      sortable: true,
    },
    {
      name: "building",
      displayName: "REPORT.BUILDING",
      size: 3,
      sortable: true,
      colValue: (row) => {
        return row.building.name;
      },
    },
    { 
      name: "title", 
      displayName: "REPORT.TITLE", 
      size: 3, 
      sortable: true 
    },
    {
      name: "priority",
      displayName: "REPORT.PRIORITY",
      size: 3,
      sortable: true,
    },
    { 
      name: "status", 
      displayName: "REPORT.STATUS", 
      size: 3, 
      sortable: true 
    },
  ];

  options: PaginationClientSideOptions = {
    ...new PaginationClientSideOptions(),
    mobileViewBreakPoint: 1270,
    headerStickyPosition: 395,
    mobileFilterButton: true,
    gridColumnsCount: 24,
    styleClasses: ["content-table"],
  };

  datePipe = new DateStiPipe(this.userServ);

  public chartOptions: Partial<ChartOptions>;
  chartHeaderSize: string;
  localePl = require("node_modules/apexcharts/dist/locales/pl.json");
  localeEn = require("node_modules/apexcharts/dist/locales/en.json");

  constructor(
    public p: ThisPlatform,
    private userServ: UserService,
    public modalCtrl: ModalController,
    public dictionaryServ: DictionaryService,
    public reportServ: ReportService,
    public buildingServ: BuildingService,
    public translateServ: TranslateService
  ) { }

  async ngOnInit() {
    this.getSubmissionReports();

    if (this.p.renderMobile) {
      this.chartHeaderSize = "14px";
    } else {
      this.chartHeaderSize = "28px";
    }

    await this.buildingServ.loadBuildingsSync();
    
    this.reportServ.actualSubscription = this.reportServ.filterDetailsSubject.subscribe(
      () => {
        this.getSubmissionReports();
      }
    );
  }

  async onToggleFilter() {
    const modal = await this.modalCtrl.create({
      component: ReportFilterModalComponent,
      componentProps: {
        page: "report",
      },
      canDismiss: preventSwipeToClose,
      cssClass: "add-edit-modal mobile-filter__modal-container",
    });
    modal.present();
  }

  onFilterStatus(status) {
    if (status.length !== 0) {
      this.reportServ.reportFilter.status = status ? status : null;
      this.reportServ.reportFilter.statusValue = status
        ? status.value.toString()
        : null;
    } else {
      this.reportServ.reportFilter.statusValue = "null";
      this.reportServ.reportFilter.status = null;
    }
    this.getSubmissionReports();
  }

  onFilterPriority(priority) {
    if (priority.length !== 0) {
      this.reportServ.reportFilter.priority = priority ? priority : null;
      this.reportServ.reportFilter.priorityValue = priority
        ? priority.value.toString()
        : null;
    } else {
      this.reportServ.reportFilter.priority = null;
      this.reportServ.reportFilter.priorityValue = "null";
    }
    this.getSubmissionReports();
  }

  getSubmissionReports() {
    this.showLoader = true;
    this.reportServ.filterDetailsShowLoader = true;
    this.reportServ.getReports({
      status:
        this.reportServ.reportFilter.statusValue == "null"
        ? null
        : this.reportServ.reportFilter.statusValue,
      priority:
        this.reportServ.reportFilter.priorityValue == "null"
        ? null
        : this.reportServ.reportFilter.priorityValue,
    }).subscribe((res) => {
      this.reportServ.reports = this.reportsFilter = res;
      this.loadChart(res)
      this.rowsLoaderInit();
    });
  }

  rowsLoaderInit() {
    if (this.rowsLoader) {
      this.showLoader = false;
      this.reportServ.filterDetailsShowLoader = false;
      this.rowsLoader.init(this.reportsFilter);
    }
  }

  rowsLoaderServiceReady(loader: RowsLoaderService) {
    this.showLoader = false;
    this.rowsLoader = loader;
    this.rowsLoaderInit();
  }

  async loadChart(
    data: Report[]
  ) {
    const newStatus = data
      .filter(el => el.status == ReportStatus.NewStatus)
      .map(el => el.status);

    const assignedStatus = data
      .filter(el => el.status == ReportStatus.AssignedStatus)
      .map(el=>el.status);

    const doneStatus = data
      .filter(el=>el.status == ReportStatus.DoneStatus)
      .map(el=>el.status);
    
    const closedStatus = data
      .filter(el=>el.status == ReportStatus.ClosedStatus)
      .map(el=>el.status);

    this.chartOptions = {
      series: [
        {
          name: this.translateServ.instant("ADMIN.SUBMISSION.AMOUNT"),
          data: [
            newStatus.length, 
            assignedStatus.length, 
            doneStatus.length, 
            closedStatus.length
          ]
        },
      ],
      chart: {
        height: 250,
        type: "bar",
        zoom: {
          enabled: true,
          type: 'x',  
          autoScaleYaxis: false,  
        },
        defaultLocale: this.userServ.getDefaultLanguage(),
        locales: [ this.localePl, this.localeEn ],
        toolbar: {
          tools: {
            download: false,
          }
        },
      },
      colors: ["#D60D8C", "#4285F4"],
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 2,
      },
      title: {
        text: this.translateServ.instant(
          "ADMIN.SUBMISSION.HEADER"
        ),
        align: "left",
        margin: 10,
        offsetX: 10,
        style: {
          fontSize: this.chartHeaderSize,
          fontWeight: 'bold',
        },
      },
      markers: {
        size: 0,
        hover: {
          sizeOffset: 6
        }
      },
      xaxis: {
        tickPlacement: 'on',
        categories: [
          this.translateServ.instant("ADMIN.SUBMISSION.CATEGORY.NEW"), 
          this.translateServ.instant("ADMIN.SUBMISSION.CATEGORY.ASSIGNED"), 
          this.translateServ.instant("ADMIN.SUBMISSION.CATEGORY.DONE"), 
          this.translateServ.instant("ADMIN.SUBMISSION.CATEGORY.CLOSED")
        ],
      },
      grid: {
        row: {
          colors: ['#fff', '#f2f2f2']
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: "horizontal",
          shadeIntensity: 0.25,
          gradientToColors: undefined,
          inverseColors: true,
          opacityFrom: 0.85,
          opacityTo: 0.85,
          stops: [50, 0, 100]
        },
      },
      noData: {
        text: this.translateServ.instant(
          "NO_DATA_TO_SHOW"
        ),
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
          fontSize: '18px',
        }
      }
    }
  }

}
